import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './App.scss';
import Router from './router/Router';
import {authSelector, loadCurrentUser} from '../store/auth';
import {appDataLoadSettings, appDataSettingsSelector} from '../store/appData';
import {appInitializedSelector, appZoomSelector} from '../store/app';
import EmptyPageWithSpinner from './pages/empty/EmptyPageWithSpinner';
import {Dialog} from '@mui/material';
import {LoginPage} from './auth/LoginPage';
import {useAuthExpirationListener} from '../store/authExpiration';
import SessionWillExpire from './auth/SessionWillExpire';
import {useTracking} from '../tracking/tracking';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {useLocation} from 'react-router-dom';
import {ErrorRouter} from './pages/error/ErrorRouter';
//import {LoggedOutPage} from './auth/LoggedOutPage';
import LoginHomePage from './auth/LoginHomePage';
import {useUserScenarioExecutionStatusUpdate} from '../websocket/useUserScenarioExecutionStatusUpdate';
import {useCustomSettingsLocalStorage} from '../store/customSettings';
import {usePermissions} from '../permissions/usePermissions';
import GuidingTourOrchestrator from './pages/guidedTour/GuidedTourOrchestrator';

const App: React.FC = () => {
  const zoom = useSelector(appZoomSelector);
  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
    const htmlElement = document.querySelector('html');
    if (htmlElement) {
      htmlElement.style.fontSize = `${10 * zoom}px`;
    }
  }, [zoom]);

  const dispatch = useDispatch();
  const {pathname} = useLocation();
  const isError = pathname.startsWith('/error');
  const isServerUnavailable = window.location.hash.startsWith('#/base');

  useEffect(() => {
    if (!isError || !isServerUnavailable) {
      loadCurrentUser(dispatch);
      appDataLoadSettings(dispatch);
    }
  }, [dispatch, isError, isServerUnavailable]);

  useDocumentTitle();
  useTracking();
  usePermissions();
  useAuthExpirationListener();
  useUserScenarioExecutionStatusUpdate();
  useCustomSettingsLocalStorage();
  localStorage.setItem('clientURL', window.location.origin);

  const auth = useSelector(authSelector);
  const appInitialized = useSelector(appInitializedSelector);
  const settings = useSelector(appDataSettingsSelector);

  // Save current location before authentication for deep link restoration
  // Exclude admin paths since access is permission-controlled
  useEffect(() => {
    if (
      !auth.loggedInUser &&
      !auth.isFetching &&
      window.location.hash &&
      window.location.hash !== '#/' &&
      window.location.hash !== '#/filter' &&
      window.location.hash !== '#/model' &&
      !window.location.hash.startsWith('#/admin')
    ) {
      localStorage.setItem('pendingDeepLink', window.location.hash);
    }
  }, [auth]);

  // Restore deep link after successful authentication
  useEffect(() => {
    if (auth.loggedInUser) {
      const pendingDeepLink = localStorage.getItem('pendingDeepLink');
      localStorage.removeItem('pendingDeepLink');
      if (pendingDeepLink && pendingDeepLink !== window.location.hash) {
        window.location.hash = pendingDeepLink;
      }
    }
  }, [auth.loggedInUser]);

  if (isError || isServerUnavailable) {
    return <ErrorRouter />;
  }
  if (!appInitialized) {
    return <EmptyPageWithSpinner />;
  }

  // Auth still loading (neither loggedInUser nor loggedOutUser set yet)
  if (!auth.loggedInUser && !auth.loggedOutUser && auth.isFetching) {
    return <EmptyPageWithSpinner />;
  }

  return (
    <>
      {!auth.loggedInUser && (
        <Dialog fullScreen open={true}>
          {settings?.ssoEnabled ? <LoginHomePage /> : <LoginPage />}
        </Dialog>
      )}

      {(auth.loggedInUser || auth.loggedOutUser) && <Router />}
      {auth.loggedInUser && <GuidingTourOrchestrator />}
      {auth.loggedInUser && <SessionWillExpire />}
    </>
  );
};

export default App;
